/* @path: /src/managers/workspaceManager.js */
import { authManager } from '@/managers/AuthManager';
import { config } from '@/utils/setProdOrDev';
import { normalizeWorkspaceContextId, isDefaultWorkspaceContextId } from '@/utils/workspaceContext';
import { mergeWorkspaceAccessSettings, normalizeWorkspaceAccessSettings } from '@/utils/workspaceAccessSettings';

export { DEFAULT_WORKSPACE_ID } from '@/utils/workspaceContext';

export const WORKSPACE_CHANGED_EVENT = 'workspace:changed';

export const DEFAULT_WORKSPACE = {
    id: null,
    workspaceName: 'Main Workspace',
    status: 'ACTIVE',
    summary: null,
    workspaceAccessSettings: { activeToolIds: null, activeServerIds: null, toolIterationLimit: null },
    fileCount: 0,
    repoUrl: null,
    remoteStatus: null,
    isDefault: true,
};

let apiManagerPromise = null;
const getApiManager = async () => {
    if (!apiManagerPromise) {
        apiManagerPromise = import('@/components/layout/RightPanel/shelbulaworkspaces/apiManager').then(m => m.apiManager);
    }
    return apiManagerPromise;
};

let state = {
    workspaces: [],
    isLoading: true,
    error: null,
    hasLoadedListOnce: false,
    hasPendingApproval: false,
    activeWorkspaceId: null,
    activeWorkspaceDetail: null,
    isDetailLoading: false,
    detailError: null,
    lastLoadedDetailWorkspaceId: null,
};

const listeners = new Set();
let isConnected = false;

// Simple deep compare for workspace arrays
const hasChanged = (oldData, newData) => {
    return JSON.stringify(oldData) !== JSON.stringify(newData);
};

const setState = (newState) => {
    state = { ...state, ...newState };
    listeners.forEach(listener => listener(state));
};

const applyUpdatedWorkspaceToState = (updatedWorkspace) => {
    if (!updatedWorkspace?.id) {
        return;
    }

    const nextWorkspaces = state.workspaces.map((workspace) => (
        workspace.id === updatedWorkspace.id ? { ...workspace, ...updatedWorkspace } : workspace
    ));

    const nextActiveWorkspaceDetail = state.activeWorkspaceDetail?.id === updatedWorkspace.id
        ? { ...state.activeWorkspaceDetail, ...updatedWorkspace }
        : state.activeWorkspaceDetail;

    setState({
        workspaces: nextWorkspaces,
        activeWorkspaceDetail: nextActiveWorkspaceDetail,
    });
};

const subscribe = (listener) => {
    listeners.add(listener);
    listener(state);
    return () => listeners.delete(listener);
};

const resolveMainWorkspace = (workspaces) => {
    if (!Array.isArray(workspaces) || workspaces.length === 0) {
        return null;
    }

    return workspaces.find((workspace) => isDefaultWorkspaceContextId(workspace.id)) || workspaces[0] || null;
};

const setActiveWorkspace = (workspaceId) => {
    const nextWorkspaceId = normalizeWorkspaceContextId(workspaceId);
    if (state.activeWorkspaceId === nextWorkspaceId) return;
    if (!nextWorkspaceId) {
        setState({
            activeWorkspaceId: null,
            activeWorkspaceDetail: null,
            isDetailLoading: false,
            detailError: null,
        });
        window.dispatchEvent(new CustomEvent(WORKSPACE_CHANGED_EVENT, { detail: { activeWorkspaceId: null } }));
        return;
    }
    const existingWorkspaceDetail = state.workspaces.find((workspace) => workspace.id === nextWorkspaceId);
    setState({
        activeWorkspaceId: nextWorkspaceId,
        activeWorkspaceDetail: existingWorkspaceDetail || null,
        isDetailLoading: true,
        detailError: null
    });
    fetchWorkspaceDetail(nextWorkspaceId, true).catch(() => null);
    window.dispatchEvent(new CustomEvent(WORKSPACE_CHANGED_EVENT, { detail: { activeWorkspaceId: nextWorkspaceId } }));
};

const fetchWorkspaceList = async (isInitial = false) => {
    try {
        const apiManager = await getApiManager();
        const result = await apiManager.listAllWorkspacesWithDetails();
        const workspacesData = result.workspaces || [];

        const nextHasPendingApproval = workspacesData.some(ws => ws.status === 'PENDING_APPROVAL');
        const shouldUpdateState =
            hasChanged(state.workspaces, workspacesData)
            || state.isLoading
            || state.error !== null
            || state.hasPendingApproval !== nextHasPendingApproval
            || !state.hasLoadedListOnce;

        if (shouldUpdateState) {
            const mainWorkspace = resolveMainWorkspace(workspacesData);
            const activeWorkspaceStillExists = state.activeWorkspaceId
                ? workspacesData.some((workspace) => workspace.id === state.activeWorkspaceId)
                : false;
            const shouldInitActiveWorkspace = workspacesData.length > 0 && !activeWorkspaceStillExists;
            const nextActiveId = shouldInitActiveWorkspace
                ? (mainWorkspace?.id || null)
                : state.activeWorkspaceId;
            const nextActiveDetail = shouldInitActiveWorkspace
                ? (workspacesData.find((workspace) => workspace.id === nextActiveId) || null)
                : state.activeWorkspaceDetail;
            setState({
                workspaces: workspacesData,
                isLoading: false,
                error: null,
                hasPendingApproval: nextHasPendingApproval,
                hasLoadedListOnce: true,
                activeWorkspaceId: nextActiveId,
                activeWorkspaceDetail: nextActiveDetail,
            });
        }

        return workspacesData;
    } catch (err) {
        console.error('[WorkspaceManager] Fetch list error:', err);

        const shouldSurfaceError = isInitial || !state.hasLoadedListOnce;
        if (shouldSurfaceError && state.error !== err.message) {
            setState({ error: err.message, isLoading: false });
        }

        return null;
    }
};

const fetchWorkspaceDetail = async (workspaceId, isInitial = false) => {
    try {
        const currentListDetail = state.workspaces.find((ws) => ws.id === workspaceId) || null;
        const detail = currentListDetail || null;

        if (detail) {
            const mergedDetail = {
                ...detail,
                comprehension: detail.comprehension || state.activeWorkspaceDetail?.comprehension,
                cleanedRequest: detail.cleanedRequest || state.activeWorkspaceDetail?.cleanedRequest
            };

            const shouldUpdateState =
                hasChanged(state.activeWorkspaceDetail, mergedDetail)
                || state.isDetailLoading
                || state.detailError !== null
                || state.lastLoadedDetailWorkspaceId !== workspaceId;

            if (shouldUpdateState) {
                setState({
                    activeWorkspaceDetail: mergedDetail,
                    isDetailLoading: false,
                    detailError: null,
                    lastLoadedDetailWorkspaceId: workspaceId,
                });
            }
            return mergedDetail;
        }

        const refreshedWorkspaces = await fetchWorkspaceList(false);
        const refreshedDetail = refreshedWorkspaces?.find((ws) => ws.id === workspaceId) || null;

        if (refreshedDetail) {
            const mergedDetail = {
                ...refreshedDetail,
                comprehension: refreshedDetail.comprehension || state.activeWorkspaceDetail?.comprehension,
                cleanedRequest: refreshedDetail.cleanedRequest || state.activeWorkspaceDetail?.cleanedRequest,
            };

            setState({
                activeWorkspaceDetail: mergedDetail,
                isDetailLoading: false,
                detailError: null,
                lastLoadedDetailWorkspaceId: workspaceId,
            });
            return mergedDetail;
        }

        if (!state.detailError) {
            setState({ detailError: 'Workspace not found', isDetailLoading: false });
        }
        return null;
    } catch (err) {
        console.error('[WorkspaceManager] Fetch detail error:', err);

        const shouldSurfaceError = isInitial || state.lastLoadedDetailWorkspaceId !== workspaceId;
        if (shouldSurfaceError && state.detailError !== err.message) {
            setState({ detailError: err.message, isDetailLoading: false });
        }

        return null;
    }
};

const connectListStream = async () => {
    if (isConnected) {
        await refreshNow();
        return;
    }
    
    const authState = authManager.getState();
    if (!authState.user || !authState.isInitialized) {
        const unsubAuth = authManager.subscribe((newAuthState) => {
            if (newAuthState.user && newAuthState.isInitialized) {
                unsubAuth();
                connectListStream();
            }
        });
        return;
    }
    
    isConnected = true;
    setState({ isLoading: true, error: null });

    await fetchWorkspaceList(true);
};

const disconnect = () => {
    isConnected = false;
    setState({
        workspaces: [], activeWorkspaceId: null, activeWorkspaceDetail: null,
        isLoading: false, isDetailLoading: false, error: null, detailError: null, hasPendingApproval: false,
        hasLoadedListOnce: false, lastLoadedDetailWorkspaceId: null,
    });
};

const refreshNow = async () => {
    const refreshedWorkspaces = await fetchWorkspaceList(false);

    if (state.activeWorkspaceId && Array.isArray(refreshedWorkspaces)) {
        const detail = refreshedWorkspaces.find((ws) => ws.id === state.activeWorkspaceId) || null;
        if (detail) {
            setState({
                activeWorkspaceDetail: detail,
                isDetailLoading: false,
                detailError: null,
                lastLoadedDetailWorkspaceId: state.activeWorkspaceId,
            });
        }
    }
};

const updateWorkspaceAccessSettings = async (workspaceId, partialSettings) => {
    const normalizedWorkspaceId = normalizeWorkspaceContextId(workspaceId);
    if (!normalizedWorkspaceId) {
        return null;
    }

    const workspace = state.activeWorkspaceDetail?.id === normalizedWorkspaceId
        ? state.activeWorkspaceDetail
        : state.workspaces.find((entry) => entry.id === normalizedWorkspaceId);

    const mergedAccessSettings = mergeWorkspaceAccessSettings(workspace, partialSettings);
    const normalizedAccessSettings = normalizeWorkspaceAccessSettings(mergedAccessSettings);
    const apiManager = await getApiManager();
    const result = await apiManager.updateWorkspaceDetails(null, normalizedWorkspaceId, {
        workspaceAccessSettings: normalizedAccessSettings,
    });

    if (result?.updatedWorkspace) {
        applyUpdatedWorkspaceToState(result.updatedWorkspace);
    }

    return result?.updatedWorkspace || null;
};

export const workspaceManager = {
    subscribe,
    getState: () => state,
    setActiveWorkspace,
    connectListStream,
    disconnect,
    fetchWorkspaces: refreshNow,
    refreshNow,
    updateWorkspaceAccessSettings,
};